import React, {useCallback, useMemo} from "react";
import "./DashboardReportWrapper.scss"
import {Dashboard} from "api/model/dashboard/Dashboard";
import {DateUtils} from "utils/time-utils";
import {
    DashboardDateVariable,
    DashboardHtmlVariable,
    DashboardReportMarginBoxes,
    DashboardVariable
} from "api/model/dashboard/DashboardReportOptions";
import {ComponentGroupLayoutWrapper} from "../dashboard-report/DashboardReportPage";
import {
    dashboardComponentStateCallback,
    getDashboardComponentState,
    getDashboardState,
    setDashboardState
} from "store/window-dashboard-store";
import {DashboardComponentMappingWrapper, getDashboardId} from "../dashboard/components/dashboard-utils";
import {DashboardComponentState} from "../dashboard/components/interfaces";
import {DashboardComponent} from "api/model/dashboard/DashboardComponent";
import {getComponentType} from "../../ComponentType";
import {ChronoUnit, DateTimeFormatter, Instant, ZoneId} from "@js-joda/core";
import {JodaTimeUtils} from "utils/joda-time-utils";
import truncateAndOffsetStr = JodaTimeUtils.truncateAndOffsetStr;

declare global {
    interface Window {
        reportGenerationOptions?: {
            companyRef: string,
            dashboardId: string,
            dashboardTitle: string,
            referenceTime: string,
            easasUrl: string,
            scheduleTime: string | null,
            genStartTime: string,
        }
    }
}

type ReportWrapperProps = {
    dashboard: Dashboard,
    referenceTime: string,
    children: JSX.Element | JSX.Element[]
}

type DashboardReportComponentProps = {
    header: string,
    children: JSX.Element
}


const DashboardReportWrapper = (props: {dashboard: Dashboard, referenceTime: string, finishCallback: (status: "finished"| "error") => void}) => {
    const allComponentIds = useMemo(() => {
        const componentIds: Record<string, DashboardComponent> = {};
        props.dashboard.dashboardTabsJs?.forEach((t, tabIndex) => {
            t.componentGroups?.forEach((g, groupIndex) => {
                g.components?.forEach((c, componentIndex) => {
                    const typeMapped = getComponentType(c.type);
                    if(typeMapped?.supportsStatusCallback) {
                        componentIds[getDashboardId(c.type, tabIndex, groupIndex, componentIndex)] = c;
                    }
                })
            })
        })
        return componentIds;
    }, [props.dashboard.dashboardTabsJs])

    const componentStateCb = useCallback((state: DashboardComponentState) => {
        dashboardComponentStateCallback(state);

        const allCount = Object.keys(allComponentIds).length;
        const finishedCount = Object.keys(allComponentIds).filter(id => {
            const state = getDashboardComponentState(id);
            return state?.state === "finished";
        }).length;
        const percent = Math.round((finishedCount / allCount) * 100);
        console.log(`Status of dashboard: ${state.id} -> ${state.state} (${finishedCount}/${allCount} = ${percent}% finished)`);

        if(getDashboardState() === "finished" || getDashboardState() == "error") return;

        if(state.state === "error") {
            props.finishCallback("error");
            setDashboardState("error");
        }

        const allFinished = Object.keys(allComponentIds).every(id => {
            const state = getDashboardComponentState(id);
            return state?.state === "finished";
        });


        if(allFinished) {
            props.finishCallback("finished");
            setDashboardState("finished");
        }
    }, [props.dashboard])

    return <ReportWrapper dashboard={props.dashboard} referenceTime={props.referenceTime}>
        <React.Fragment>
            {props.dashboard.dashboardTabsJs?.map((c, tabIdx) => {
                return <React.Fragment key={tabIdx}>
                    {c.componentGroups?.map((g, groupIdx) => {
                        return <ComponentGroupLayoutWrapper key={groupIdx} group={g}>

                            {g.components?.map((component, componentIdx) => {
                                return <React.Fragment key={componentIdx}>
                                    <DashboardReportComponent header={component.title}>
                                        <DashboardComponentMappingWrapper
                                            id={getDashboardId(component.type, tabIdx, groupIdx, componentIdx)}
                                            component={component.type}
                                            config={component.config}
                                            referenceTime={props.referenceTime}
                                            stateCb={componentStateCb}
                                        />
                                    </DashboardReportComponent>
                                </React.Fragment>
                            })}
                        </ComponentGroupLayoutWrapper>
                    })}
                </React.Fragment>
            })}
        </React.Fragment>
    </ReportWrapper>
}


const ReportWrapper = ({children, dashboard, referenceTime}: ReportWrapperProps) => {
    const DEFAULT_PATTERN = "yyyy-MM-dd HH:mm";
    const marginBoxes = dashboard.reportOptions.marginBoxes;

    const processVariables = (html: string) => {
        const pattern = dashboard.reportOptions.timingOptions.reportTimeFormat ?? DEFAULT_PATTERN;

        const scheduleTimeReplacer = () => {
            if(!window.reportGenerationOptions?.scheduleTime) return 'NOT-SCHEDULED';
            return DateUtils.strFormatDateTime(window.reportGenerationOptions?.scheduleTime, pattern);
        }

        const generationTimeReplacer = () => {
            if(!window.reportGenerationOptions?.genStartTime) return 'NOT-GENERATED';
            return DateUtils.strFormatDateTime(window.reportGenerationOptions?.genStartTime, pattern);
        }

        const referenceTimeReplacer = () => {
            return DateUtils.strFormatDateTime(referenceTime, pattern);
        }

        const replacer: Record<string, string> = {
            'scheduleTime':     scheduleTimeReplacer(),
            'referenceTime':    referenceTimeReplacer(),
            'generationTime':   generationTimeReplacer(),
            'totalPages':       '<span class="page-number-total"></span>',
            'currentPage':      '<span class="page-number-current"></span>',
        }

        let processed = html;
        Object.keys(replacer).forEach(kv => {
            const value = replacer[kv];
            const regex = new RegExp(`\\$${kv}`, 'g');
            processed = processed.replace(regex, value);
        });

        return processed;
    }

    const variableValueMapper = (value: DashboardVariable, dashboard: Dashboard) => {
        switch (value.type) {
            case "DATE":
                const defaults: Partial<DashboardDateVariable> = {
                    truncateTo: ChronoUnit.HOURS.toString(),
                    offsetUnit: ChronoUnit.HOURS.toString(),
                    offset: 0,
                    format: DEFAULT_PATTERN
                }

                let dateObj = value as DashboardDateVariable;
                // apply defaults
                dateObj = {...defaults, ...dateObj};

                const zoneId = ZoneId.of(dashboard.reportOptions.timingOptions.timeZoneId);
                let date = Instant.now().atZone(zoneId).toLocalDateTime();
                date = truncateAndOffsetStr(date, dateObj.truncateTo!!, dateObj.offset!!, dateObj.offsetUnit!!);

                return date.format(DateTimeFormatter.ofPattern(dateObj.format!!))
            case "HTML":
                const htmlObj = value as DashboardHtmlVariable;
                return htmlObj.content;
        }
    }


    /**
     * Variables prefixed with $ are replaced with builtin values or values from dashboard.reportOptions.variables
     * @param marginBox margin box name to process
     */
    const preformatMarginBox = (marginBox: keyof DashboardReportMarginBoxes) => {
        const box = marginBoxes?.[marginBox];
        const html = box?.content;
        if(!html) return undefined;

        // Fixed variables processing
        // TODO: when there is more builtin values make replacer map struct to cleanup this code
        let processed = processVariables(html)



        // Margin box dynamic variables processing
        const boxVariables = box.variables;
        if(boxVariables) {
            Object.keys(boxVariables).forEach(kv => {
                const value = boxVariables[kv];
                const regex = new RegExp(`\\$${kv}`, 'g');
                processed = processed.replace(regex, value);
            });
        }

        // Global variables processing
        const globalVariables = dashboard.reportOptions.variables;
        if(globalVariables) {
            Object.keys(globalVariables).forEach(kv => {
                const objValue = globalVariables[kv];
                const regex = new RegExp(`\\$${kv}`, 'g');
                const value = variableValueMapper(objValue, dashboard);
                processed = processed.replace(regex, value);
            });
        }

        return {__html: processed}
    }

    return <div id="dashboard-report-wrapper-paged" className="dashboard-report-wrapper-paged print-mode-enabled">
        {/* ==================================================== Margin Boxes ================================================*/}
        <div className="topLeftMarginBox"       dangerouslySetInnerHTML={preformatMarginBox('topLeft')}></div>
        <div className="topCenterMarginBox"     dangerouslySetInnerHTML={preformatMarginBox('topCenter')}></div>
        <div className="topRightMarginBox"      dangerouslySetInnerHTML={preformatMarginBox('topRight')}></div>
        <div className="bottomLeftMarginBox"    dangerouslySetInnerHTML={preformatMarginBox('bottomLeft')}></div>
        <div className="bottomCenterMarginBox"  dangerouslySetInnerHTML={preformatMarginBox('bottomCenter')}></div>
        <div className="bottomRightMarginBox"   dangerouslySetInnerHTML={preformatMarginBox('bottomRight')}></div>

        {/* ==================================================== Dashboad Components ============================================*/}
        {children}
    </div>
}

const DashboardReportComponent = ({header, children}: DashboardReportComponentProps) => {
    return <div className="dashboard-report-component">
        { header &&
            <div className="dashboard-report-component-header">
                <h3>{header}</h3>
            </div>
        }

        <div className="dashboard-report-component-body">
            { React.cloneElement(children) }
        </div>
    </div>
}

export default DashboardReportWrapper;
